import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  useApplyTenantSelection,
  type ApplyTenantSelectionArgs,
} from "../../../hooks/useApplyTenantSelection";
import { queryKeys } from "../../../services/queryKeys";

export interface UseSwitchTenantModalInternalStateArgs {
  onClose: () => void;
}

/**
 * Finishes a tenant switch made from inside the app.
 *
 * The token and workspace list were already fetched by `TenantSelectForm`, so
 * this only (1) persists the choice, (2) resets every cached query except the
 * tenant-context ones — they hold the previous tenant's data and the active
 * ones refetch under the new token — and (3) lands on /home.
 */
export const useSwitchTenantModalInternalState = ({
  onClose,
}: UseSwitchTenantModalInternalStateArgs) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const applyTenantSelection = useApplyTenantSelection();

  const handleContinue = useCallback(
    (selection: ApplyTenantSelectionArgs) => {
      applyTenantSelection(selection);
      void queryClient.resetQueries({
        predicate: (query) => query.queryKey[0] !== queryKeys.tenantContext.all[0],
      });
      onClose();
      navigate("/home", { replace: true });
    },
    [applyTenantSelection, queryClient, navigate, onClose],
  );

  return { handleContinue };
};
